<div class="mx-auto max-w-(--breakpoint-2xl)">
  @if (template) {
    <div class="relative rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
      <div class="p-5 mb-6 border border-gray-200 rounded-2xl dark:border-gray-800 lg:p-6">
        <div class="flex flex-col mb-8 justify-start gap-5 xl:flex-row">
          <app-badge
            [text]="template.event_type"
            [color]="getEventTypeColor(template.event_type)">
          </app-badge>
        </div>

        <div class="order-3 xl:order-2">
          <h4 class="mb-2 text-2xl font-semibold text-center text-gray-800 dark:text-white/90 xl:text-left">
            {{ template.name }}
          </h4>
        </div>

        <div class="mt-8 flex flex-col gap-4">
          <div class="pl-6 flex flex-row justify-end">
            <app-button
              [label]="'Duplicate'"
              [icon]="PlusIcon"
              [style]="'secondary'"
              [disabled]="!isAdmin"
              [title]="!isAdmin ? 'Only admins can duplicate templates' : ''"
              (click)="duplicateTemplate()">
            </app-button>
            <app-button
              [label]="'Edit'"
              [icon]="EditIcon"
              class="ml-2"
              [disabled]="!isAdmin"
              [title]="!isAdmin ? 'Only admins can edit templates' : ''"
              (click)="editTemplate()">
            </app-button>
            <app-button
              [label]="deleting ? 'Deleting...' : 'Delete'"
              [icon]="Trash2Icon"
              [style]="'danger'"
              class="ml-2"
              [disabled]="!isAdmin || deleting"
              [title]="!isAdmin ? 'Only admins can delete templates' : ''"
              (click)="deleteTemplate()">
            </app-button>
          </div>
        </div>
      </div>

      <div class="mt-8">
        <div class="p-5 border border-gray-200 rounded-2xl dark:border-gray-800 lg:p-6">
          <h3 class="mb-4 text-base font-medium text-gray-800 dark:text-white/90">
            Template Information
          </h3>

          @if (template.description) {
            <div class="mt-4">
              <p class="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                Description
              </p>
              <p class="text-sm font-medium text-gray-800 dark:text-white/90 mb-4">
                {{ template.description }}
              </p>
            </div>
          }

          <div class="grid grid-cols-1 gap-4 lg:grid-cols-4 lg:gap-7 mt-4">
            <div>
              <p class="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                <lucide-icon [name]="BuildingIcon" [size]="16" class="inline mr-1 text-gray-400 dark:text-gray-500"></lucide-icon>
                Venue
              </p>
              <p class="text-sm font-medium text-gray-800 dark:text-white/90">
                {{ template.venue?.name || 'All Venues' }}
              </p>
            </div>

            <div>
              <p class="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                <lucide-icon [name]="CalendarIcon" [size]="16" class="inline mr-1 text-gray-400 dark:text-gray-500"></lucide-icon>
                Created
              </p>
              <p class="text-sm font-medium text-gray-800 dark:text-white/90">
                {{ template.created_at | date:'mediumDate' }}
              </p>
            </div>

            <div>
              <p class="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                <lucide-icon [name]="ClockIcon" [size]="16" class="inline mr-1 text-gray-400 dark:text-gray-500"></lucide-icon>
                Timeline Items
              </p>
              <p class="text-sm font-medium text-gray-800 dark:text-white/90">
                {{ template.items?.length || 0 }}
              </p>
            </div>

            <div>
              <p class="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                <lucide-icon [name]="UsersIcon" [size]="16" class="inline mr-1 text-gray-400 dark:text-gray-500"></lucide-icon>
                Used in Events
              </p>
              @if (template.events && template.events.length > 0) {
                <div class="space-y-1">
                  @for (event of template.events; track event.id) {
                    <div class="flex items-center gap-2">
                      <a 
                        [routerLink]="['/events', event.id]"
                        class="text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                      >
                        {{ event.name }}
                      </a>
                      <app-badge
                        [text]="event.status"
                        [color]="getEventStatusColor(event.status)"
                        class="text-xs"
                      ></app-badge>
                    </div>
                    @if (event.start_date) {
                      <p class="text-xs text-gray-500 dark:text-gray-400 ml-0">
                        {{ event.start_date | date:'mediumDate' }}
                      </p>
                    }
                  }
                </div>
              } @else {
                <p class="text-sm font-medium text-gray-800 dark:text-white/90">
                  No events using this template
                </p>
              }
            </div>
          </div>

          @if (template.creator) {
            <div class="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
              <p class="text-xs leading-normal text-gray-500 dark:text-gray-400">
                Created by <span class="font-medium">{{ template.creator.name }}</span>
                @if (template.updater && template.updater.id !== template.creator.id) {
                  · Last updated by <span class="font-medium">{{ template.updater.name }}</span>
                }
              </p>
            </div>
          }
        </div>
      </div>

      <!-- Timeline Items -->
      <div class="mt-8">
        <div class="p-5 border border-gray-200 rounded-2xl dark:border-gray-800 lg:p-6">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-base font-medium text-gray-800 dark:text-white/90">
              Timeline Items
            </h3>
            <app-button
              [label]="applyingTemplate ? 'Applying...' : 'Apply to Event'"
              [icon]="CalendarIcon"
              [style]="canApplyTemplates ? 'primary' : 'secondary'"
              [disabled]="!canApplyTemplates || applyingTemplate || isRightSidebarOpen"
              [title]="!canApplyTemplates ? 'Only admins or managers can apply templates' : ''"
              (click)="applyToEvent()">
            </app-button>
          </div>

          @if (getSortedItems().length === 0) {
            <div class="text-center py-8 text-gray-500 dark:text-gray-400">
              <lucide-icon [name]="CalendarIcon" [size]="48" class="mx-auto mb-4 text-gray-300 dark:text-gray-700"></lucide-icon>
              <p class="text-lg font-medium">No timeline items</p>
              <p>This template doesn't have any timeline items yet.</p>
            <app-button
              [label]="'Add Timeline Items'"
              [icon]="EditIcon"
              [style]="'secondary'"
              class="mt-4"
              [disabled]="!isAdmin"
              [title]="!isAdmin ? 'Only admins can add timeline items' : ''"
              (click)="editTemplate()">
            </app-button>
            </div>
          } @else {
            <div class="space-y-4">
              @for (item of getSortedItems(); track item.id) {
                <div class="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-white/[0.03]">
                  <div class="flex items-start justify-between">
                    <div class="flex-1">
                      <div class="flex items-center gap-3 mb-2">
                        <h3 class="font-medium text-gray-900 dark:text-white/90">{{ item.name }}</h3>
                        <div class="flex items-center gap-2">
                          <span class="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded dark:bg-blue-900/30 dark:text-blue-300">
                            {{ formatItemOffset(item) }}
                          </span>
                          <span class="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded dark:bg-gray-800 dark:text-gray-300">
                            {{ formatTimeOfDay(item.time_of_day) }}
                          </span>
                        </div>
                      </div>

                      @if (item.description) {
                        <p class="text-gray-600 text-sm dark:text-gray-400">{{ item.description }}</p>
                      }
                    </div>
                  </div>
                </div>
              }
            </div>

            <!-- Timeline Summary -->
            <div class="mt-6 pt-6 border-t border-gray-100 dark:border-gray-800">
              <div class="rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
                <h3 class="text-sm font-medium text-blue-900 mb-2 dark:text-blue-300">Timeline Summary</h3>
                <p class="text-sm text-blue-700 dark:text-blue-400">
                  This template contains {{ getSortedItems().length }} timeline items spanning
                  @if (getSortedItems().length > 1) {
                    {{ getSortedItems()[0].days_offset }} to {{ getSortedItems()[getSortedItems().length - 1].days_offset }} days
                    relative to the event start date.
                  } @else {
                    the event date.
                  }
                </p>
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  } @else {
    <div class="text-center py-8">
      <p class="text-gray-500 dark:text-gray-400">Loading template...</p>
    </div>
  }
</div>

<!-- Right Sidebar -->
<app-right-sidebar [isSidebarOpen]="isRightSidebarOpen" (closed)="closeRightSidebar()">
  @if (rightSideBarContent == 'event-selection') {
    <app-event-selection-form
      [templateName]="template?.name || ''"
      (eventSelected)="onEventSelected($event)"
      (formCancelled)="closeRightSidebar()">
    </app-event-selection-form>
  }
</app-right-sidebar>
